/**
 * Словарь интерфейса: коды сервера → слова для человека (C:/tmp/rebrand/04-voice.md, раздел 2).
 *
 * ⛔ Внутренние коды наружу не выводятся: ни тип действия (set_bid), ни решение (succeeded_noop),
 * ни класс обратимости (R1), ни код агента (finance). Неизвестный код — нейтральное общее слово,
 * а не сам код: так новый тип на сервере не протечёт в интерфейс сырой строкой.
 *
 * ⚠️ Числа здесь не форматируются — их присылает сервер готовыми строками ([F-4.0.11]).
 * Даты — только словами из готовой даты (без пересчёта часовых поясов для local_date).
 */
import type { IconName, Tone } from '../components/icons/Icon.js'

/**
 * Важность пункта отчёта → метка. Пока сервер не присылает тон (good | attn | risk | fine), действует
 * временное соответствие из 04-voice, 3.12: critical → «Риск», warning → «Внимание», info → без метки.
 * «Хорошо» и «Штраф» из severity не выводятся — для них нужен тон с сервера.
 */
export function insightTone(severity: string | undefined): Tone | null {
  return severity === 'critical' ? 'risk' : severity === 'warning' ? 'attn' : null
}

/**
 * Названия агентов — одни и те же везде: список агентов, отчёты, заголовок отчёта в боте
 * (сервер: AGENT_TITLE в agents/settings.ts и REPORT_TITLE в agents/report-text.ts).
 * Управляющий называется «Главное»: вкладка «Сводка» уже есть, и отчёт под тем же словом
 * открывал бы не тот экран (04-voice, 2.7).
 */
export const AGENT_TITLE: Record<string, string> = {
  manager: 'Главное',
  finance: 'Финансы',
  ads: 'Реклама',
  stock: 'Остатки и заказы',
  customers: 'Отзывы и вопросы',
}

/** Порядок в списках: сначала «Главное» — оно сводит остальные. */
export const AGENT_ORDER: readonly string[] = ['manager', 'finance', 'ads', 'stock', 'customers']

/** У агентов нет своих цветов: различаются формой иконки и названием (06-icons, 5). */
export const AGENT_ICON: Record<string, IconName> = {
  manager: 'agent-manager',
  finance: 'agent-finance',
  ads: 'agent-ads',
  stock: 'agent-stock',
  customers: 'agent-feedback',
}

export const agentTitle = (code: string): string => AGENT_TITLE[code] ?? 'Агент'
export const agentIcon = (code: string): IconName => AGENT_ICON[code] ?? 'section-agents'

/** Что предлагает действие — инфинитивом, как в эталонах отчётов (04-voice, 4.2). */
export const ACTION_TYPE: Record<string, string> = {
  set_bid: 'Изменить ставку',
  pause_campaign: 'Приостановить кампанию',
  start_campaign: 'Запустить кампанию',
  set_minus_keywords: 'Добавить минус-фразы',
  deposit_budget: 'Пополнить бюджет кампании',
  answer_feedback: 'Ответить на отзыв',
  answer_question: 'Ответить на вопрос',
  plan_supply: 'Запланировать поставку',
  change_price: 'Изменить цену',
  advice_only: 'Совет',
}
export const actionTitle = (type: string): string => ACTION_TYPE[type] ?? 'Действие'

/** Обратимость — словами, а не «R1/R2/R3» (04-voice, 2.5). */
export const REVERSIBILITY: Record<string, string> = {
  R1: 'можно отменить', R2: 'можно отменить частично', R3: 'отменить нельзя',
}

/** Решение по действию (поле decision) — словами (04-voice, 2.5). */
export const DECISION: Record<string, string> = {
  pending: 'Ждёт решения',
  confirmed: 'Подтверждено',
  rejected: 'Отклонено',
  expired: 'Устарело',
  auto: 'Сделано автоматически',
  executing: 'Выполняется',
  succeeded: 'Выполнено',
  succeeded_noop: 'Уже было так',
  failed: 'Не выполнено',
  rolled_back: 'Отменено',
}
export const decisionWord = (d: string | null | undefined): string =>
  d === null || d === undefined ? 'Без решения' : DECISION[d] ?? 'Без решения'

/** Состояние последнего отчёта агента (04-voice, 3.10). */
export const RUN_STATUS: Record<string, string> = {
  ok: 'готов', degraded: 'готов не полностью', failed: 'не получился', skipped: 'не собирался', running: 'готовится',
}

const MONTHS = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']
const WEEKDAYS = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота']

/**
 * «2026-10-13» → «13 октября» (год — только если он не текущий, 04-voice, правила набора).
 * Дата магазина приходит строкой без часового пояса — её не пропускаем через Date,
 * чтобы не сдвинуть на сутки.
 */
export function dayMonth(ymd: string, nowYear: number = new Date().getFullYear()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd)
  if (m === null) return ymd
  const month = MONTHS[Number(m[2]) - 1]
  if (month === undefined) return ymd
  const base = `${Number(m[3])} ${month}`
  return Number(m[1]) === nowYear ? base : `${base} ${m[1]}`
}

/** Момент времени (ISO с поясом) → «13 октября» по часам телефона. */
export function dayMonthOf(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const ymd = `${y}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  return dayMonth(ymd)
}

/** Подзаголовок «Сводки»: «Вторник, 13 октября» — по часам телефона. */
export function todayLong(now: Date = new Date()): string {
  return `${WEEKDAYS[now.getDay()] ?? ''}, ${now.getDate()} ${MONTHS[now.getMonth()] ?? ''}`
}

/** Строку сервера-заголовок печатаем с заглавной (04-voice, 3.4); остальное — как есть. */
export const capitalize = (s: string): string => (s === '' ? s : s[0]!.toUpperCase() + s.slice(1))

/**
 * Подпись товара в «Остатках»: чем он отличается от соседа с тем же названием (просьба админа
 * 27.10.2026 — «по названию "Умные часы HK39" цвета не различить, хотя бы артикул напишите»).
 *
 * Артикул продавца у магазина несёт и модель, и цвет («HK39pro(черный)»), поэтому цвет карточки
 * добавляется, только если его там нет. Номер WB — последним: по нему товар ищется в кабинете.
 */
export function itemCode(i: { nm_id: number; vendor_code?: string | null; color?: string | null }): string {
  const parts: string[] = []
  const code = (i.vendor_code ?? '').trim()
  const color = (i.color ?? '').trim()
  if (code !== '') parts.push('арт. ' + code)
  if (color !== '' && !code.toLowerCase().includes(color.toLowerCase())) parts.push(color)
  parts.push('WB ' + i.nm_id)
  return parts.join(' · ')
}

/** Окно темпа продаж словами: «за сутки», «за 3 дн.» — «за выбранные 1 дн.» не читается. */
export const windowWords = (days: number): string => (days === 1 ? 'за сутки' : `за ${days} дн.`)
